// src/pages/RequestsManagement.jsx

import React, { useState, useEffect, useMemo } from 'react';
import usePersistentLanguage from '../hooks/usePersistentLanguage';
import styles from './RequestsManagement.module.css';
import { getAllRequests, updateRequestState, deleteRequest } from '../api/requestApi';
import { addProductQuantity, removeProductQuantity } from '../api/productApi';
import AppLayout from '../components/AppLayout';
import { formatDateDDMMYYYY } from '../utils/dateFormat';

// 1) Translation strings for English and Arabic
const translations = {
  en: {
    requestsManagementTitle: 'Requests Management',
    explanationText:
      'Review worker inventory requests, take action, and keep inventory updates on track.',
    failedFetch: 'Failed to fetch requests. Please try again.',
    notFound: 'Request with ID not found.',
    failedAccept: 'Failed to accept the request. Please try again.',
    failedReject: 'Failed to reject the request. Please try again.',
    failedDelete: 'Failed to delete the request. Please try again.',
    requestAccepted: 'has been accepted and processed.',
    requestRejected: 'has been rejected.',
    requestDeleted: 'has been deleted.',
    id: 'ID',
    username: 'Requester',
    productName: 'Product',
    sku: 'SKU',
    quantity: 'Qty',
    color: 'Color',
    sortName: 'Sort',
    action: 'Action',
    state: 'State',
    requestDate: 'Requested On',
    actions: 'Actions',
    accept: 'Accept',
    reject: 'Reject',
    delete: 'Delete',
    close: 'Close',
    nA: 'N/A',
    homepage: 'Homepage',
    noRequests: 'There are no pending requests right now.',
  },
  ar: {
    requestsManagementTitle: 'إدارة الطلبات',
    explanationText:
      'استعرض طلبات المخزون من الفريق واتخذ الإجراء المناسب للحفاظ على تحديثات المخزون.',
    failedFetch: 'فشل في جلب الطلبات. حاول مرة أخرى.',
    notFound: 'لم يتم العثور على طلب بالمعرف المحدد.',
    failedAccept: 'فشل في قبول الطلب. حاول مرة أخرى.',
    failedReject: 'فشل في رفض الطلب. حاول مرة أخرى.',
    failedDelete: 'فشل في حذف الطلب. حاول مرة أخرى.',
    requestAccepted: 'تم قبول الطلب ومعالجته.',
    requestRejected: 'تم رفض الطلب.',
    requestDeleted: 'تم حذف الطلب.',
    id: 'المعرف',
    username: 'صاحب الطلب',
    productName: 'المنتج',
    sku: 'رمز المنتج (SKU)',
    quantity: 'الكمية',
    color: 'اللون',
    sortName: 'الصنف',
    action: 'الإجراء',
    state: 'الحالة',
    requestDate: 'تاريخ الطلب',
    actions: 'الإجراءات',
    accept: 'قبول',
    reject: 'رفض',
    delete: 'حذف',
    close: 'إغلاق',
    nA: 'غير متوفر',
    homepage: 'الصفحة الرئيسية',
    noRequests: 'لا توجد طلبات معلقة حاليًا.',
  },
};

const RequestsManagement = () => {
  const [language, setLanguage] = usePersistentLanguage();
  const t = (key) => translations[language][key];

  const [requests, setRequests] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = useMemo(() => localStorage.getItem('authToken'), []);
  const userInfo = useMemo(() => {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return {
          userType: payload.userType,
          requesterUsername: payload.username,
        };
      } catch (error) {
        console.error('Error parsing token payload:', error);
        return { userType: null, requesterUsername: null };
      }
    }
    return { userType: null, requesterUsername: null };
  }, [token]);

  useEffect(() => {
    getAllRequests()
      .then((response) => {
        setRequests(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching requests:', error);
        setErrorMessage(t('failedFetch'));
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAccept = async (id) => {
    try {
      setLoading(true);
      const request = requests.find((req) => req._id === id);
      if (!request) {
        setErrorMessage(`${t('notFound')} ${id}.`);
        setLoading(false);
        return;
      }

      const payload = {
        sortName: request.sortName,
        color: request.color,
        quantity: request.quantity,
      };

      if (request.action === 'add') {
        await addProductQuantity(request.productSKU, payload);
      } else if (request.action === 'remove') {
        await removeProductQuantity(request.productSKU, payload);
      }

      await updateRequestState(id, 'Accepted');
      setModalMessage(`${t('id')} ${id} ${t('requestAccepted')}`);
      setShowModal(true);

      setRequests((prev) =>
        prev.map((req) => (req._id === id ? { ...req, state: 'Accepted' } : req))
      );
      setLoading(false);
    } catch (error) {
      console.error('Error in handleAccept:', error);
      setErrorMessage(t('failedAccept'));
      setLoading(false);
    }
  };

  const handleReject = async (id) => {
    try {
      setLoading(true);
      await updateRequestState(id, 'Rejected');
      setModalMessage(`${t('id')} ${id} ${t('requestRejected')}`);
      setShowModal(true);

      setRequests((prev) =>
        prev.map((req) => (req._id === id ? { ...req, state: 'Rejected' } : req))
      );
      setLoading(false);
    } catch (error) {
      console.error('Error rejecting request:', error);
      setErrorMessage(t('failedReject'));
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await deleteRequest(id);
      setModalMessage(`${t('id')} ${id} ${t('requestDeleted')}`);
      setShowModal(true);
      setRequests((prev) => prev.filter((req) => req._id !== id));
      setLoading(false);
    } catch (error) {
      console.error('Error deleting request:', error);
      setErrorMessage(t('failedDelete'));
      setLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const closeErrorBanner = () => {
    setErrorMessage(null);
  };

  const formatDate = (value) => {
    return formatDateDDMMYYYY(value, t('nA'));
  };

  return (
    <AppLayout
      title={t('requestsManagementTitle')}
      language={language}
      onToggleLanguage={() => setLanguage((prev) => (prev === 'en' ? 'ar' : 'en'))}
      userType={userInfo.userType}
      showHome
      homeLabel={t('homepage')}
      contentClassName={styles.requestsPage}
      contentProps={{ 'aria-busy': loading }}
    >
      {loading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.spinner}></div>
        </div>
      )}

      {!loading && (
        <>
          <section className={styles.heroCard}>
            <p>{t('explanationText')}</p>
          </section>

          {errorMessage && (
            <div className={`${styles.alert} ${styles.alertError}`} role="alert">
              <span>{errorMessage}</span>
              <button type="button" className={styles.alertDismiss} onClick={closeErrorBanner}>
                {t('close')}
              </button>
            </div>
          )}

          {requests.length === 0 ? (
            <div className={styles.emptyState}>{t('noRequests')}</div>
          ) : (
            <section className={styles.tableCard}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{t('id')}</th>
                    <th>{t('username')}</th>
                    <th>{t('productName')}</th>
                    <th>{t('sku')}</th>
                    <th>{t('quantity')}</th>
                    <th>{t('color')}</th>
                    <th>{t('sortName')}</th>
                    <th>{t('action')}</th>
                    <th>{t('state')}</th>
                    <th>{t('requestDate')}</th>
                    <th>{t('actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((request, index) => (
                    <tr key={request._id}>
                      <td>{index + 1}</td>
                      <td>{request.requestId}</td>
                      <td>{request.requesterUsername || t('nA')}</td>
                      <td>{request.productName}</td>
                      <td>{request.productSKU}</td>
                      <td>{request.quantity}</td>
                      <td>{request.color}</td>
                      <td>{request.sortName}</td>
                      <td>
                        <span
                          className={`${styles.badge} ${request.action === 'add' ? styles.badgeInfo : styles.badgeWarning}`}
                        >
                          {request.action}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`${styles.badge} ${request.state === 'Accepted'
                            ? styles.badgeSuccess
                            : request.state === 'Rejected'
                            ? styles.badgeDanger
                            : styles.badgeMuted}`}
                        >
                          {request.state || t('nA')}
                        </span>
                      </td>
                      <td>{formatDate(request.date)}</td>
                      <td>
                        <div className={styles.actionButtons}>
                          <button
                            className={`${styles.actionButton} ${styles.acceptButton}`}
                            onClick={() => handleAccept(request._id)}
                            disabled={request.state !== 'Null'}
                          >
                            {t('accept')}
                          </button>
                          <button
                            className={`${styles.actionButton} ${styles.rejectButton}`}
                            onClick={() => handleReject(request._id)}
                            disabled={request.state !== 'Null'}
                          >
                            {t('reject')}
                          </button>
                          <button
                            className={`${styles.actionButton} ${styles.deleteButton}`}
                            onClick={() => handleDelete(request._id)}
                          >
                            {t('delete')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </>
      )}

      {showModal && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modalContent}>
            <p>{modalMessage}</p>
            <button className={styles.modalButton} onClick={closeModal}>
              {t('close')}
            </button>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default RequestsManagement;
